/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import debounce  from "debounce";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useDispatch, useSelector } from "react-redux";
import { Copy, Edit, Trash } from "@jda/lui-common-icon-library-mui5";
import { AppBar, Box, Grid, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { LuiThreeDotMenu, LuiThreeDotMenuThumbnailType } from "@jda/lui-common-component-library-mui5";

import { useStyles } from "./styles";
import ModuleList from "../ModuleList";
import { TabPanel } from "../../shared/TabPanel/TabPanel";
import SearchBar from "../../shared/searchLists/SearchBar";
import SearchList from "../../shared/searchLists/SearchList";
import { AlertDialog } from "../../shared/dialog/AlertDialog";
import SnackbarAlert from "../../shared/snackbar/SnackbarAlert";
import { selectLoader } from "../../store/loader/loaderSelector";
import { actions, resources } from "../checkPermission/constants";
import SearchListItem from "../../shared/searchLists/SearchListItem";
import { SNACKBAR_ALERT_DEFAULT_PROPS } from "../../shared/constants";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { MESSAGES, PAGE_SIZE_APPLICATIONS, TITLES } from "./constants";
import EmptyComponent from "../../shared/emptyComponent/EmptyComponent";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { ThreeDotOption } from "../../shared/ThreeDotOption/ThreeDotOption";
import { resetModulesAction } from "../../store/module/moduleActionCreator";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import { setSnackBarDisplay } from "../../store/snackBar/snackBarActionCreator";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import { selectSelectedOrganization } from "../../store/organization/organizationSelector";
import AddApplicationDialog from "../../components/addApplicationDialog/AddApplicationDialog";
import { setSelectedApplicationAction } from "../../store/application/applicationActionCreator";
import { checkAuthzPermission, CheckAuthzPermissions } from "../checkPermission/CheckViewPermission";
import { getApplications, removeApplication, resetDataOnChangeOrganization } from "../../store/application/application";
import { selectAllApplication, selectNoApplication, selectSelectedApplication } from "../../store/application/applicationSelector";

const Application = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const showLoader = useSelector(selectLoader);
  const noApplication = useSelector(selectNoApplication);
  const applicationsData = useSelector(selectAllApplication);
  const selectedApplication = useSelector(selectSelectedApplication);
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const [value, setValue] = useState(0);
  const [action, setAction] = useState("");
  const [searchString, setSearchString] = useState("");

  const createApplicationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.CREATE, assignedPermissions.resources[resources.APPLICATION]);
  const editApplicationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.APPLICATION]);
  const deleteApplicationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.APPLICATION]);

  useEffect(() => {
    setSearchString("")
    if (selectedOrganization?.id) {
      getAllApplicationList();
    }
  }, [selectedOrganization?.id]);

  const getAllApplicationList = async () => {
    const applicationPayload = {
      pageNo: 1,
      id: selectedOrganization?.id,
      searchString: "",
      pageSize: PAGE_SIZE_APPLICATIONS,
    };
    resetDataOnChangeOrganization(dispatch);
    getApplications(dispatch, applicationPayload);
  };

  const handleMenuClick = (actionName: string) => {
    dispatch(setSelectedApplicationAction(selectedApplication));
    setAction(actionName);
  };

  const handleDialogClose = () => {
    setAction("");
  };

  const items: LuiThreeDotMenuThumbnailType[] = [
    editApplicationPermission && {
      render: <ThreeDotOption icon={<Edit fontSize="small" />} text={TITLES.EDIT} />,
      onClick: () => handleMenuClick("Edit"),
      autoClose: true,
    },
    deleteApplicationPermission && {
      render: <ThreeDotOption icon={<Trash fontSize="small" />} text={TITLES.DELETE} />,
      onClick: () => handleMenuClick("Delete"),
      autoClose: true,
    },
  ];

  const handleChange = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  const snackBarMessage = {
    show: false,
    autoHideDuration: 5000,
    message: "",
    severity: "success" as "success" | "error",
  };

  const modules = [
    {
      name: "Modules",
      component: <ModuleList />,
    },
  ];

  const errors = {
    message: "",
  };

  const handleCopy = () => {
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: MESSAGES.COPIED_TO_CLIPBOARD,
      })
    );
  }

  const handleClearSearchInput = () => {
    const organizationPayload = {
      id: selectedOrganization?.id,
      pageNo: 1,
      pageSize: PAGE_SIZE_APPLICATIONS,
      searchString: '',
    };
    getApplications(dispatch, organizationPayload);
  };

  const onSearchTextBoxChange = async (searchValue: string) => {
    setSearchString(searchValue);
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      const applicationPayload = {
        pageNo: 1,
        id: selectedOrganization?.id,
        pageSize: PAGE_SIZE_APPLICATIONS,
        searchString: searchValue.trim(),
      };
      resetDataOnChangeOrganization(dispatch);
      await getApplications(dispatch, applicationPayload);
    }
    dispatch(hideLoaderAction(MESSAGES.APPLICATION_TITLE));
  };

  const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, listItem: any) => {
    dispatch(setSelectedApplicationAction(listItem));
    dispatch(resetModulesAction());
  };

  const deleteApplication = async () => {
    const payload = {
      id: selectedApplication.id,
      name: selectedApplication.name,
      organizationId: selectedOrganization?.id
    }
    await removeApplication(dispatch, payload);
    setAction("");
    dispatch(hideLoaderAction(MESSAGES.APPLICATION_TITLE));
  };

  const debounceSearch = debounce(onSearchTextBoxChange, 500);

  return (
    <Box className={classes.root} height={height}>
      <CheckAuthzPermissions action={actions.VIEW} resource={resources.APPLICATION}>
        <Grid container>
          <Grid item xs={12} sm={9}>
            <Typography variant="h6" className={classes.componentHeading}>
              {MESSAGES.APPLICATION_TITLE}
            </Typography>
          </Grid>
        </Grid>
        {(applicationsData && applicationsData.length === 0 && !searchString.length) &&
          <Grid container className={classes.emptyScreenRoot}>
            <Grid item xs={12} sm={12}>
              <EmptyComponent
                subTitle=""
                disableCreateAction={false}
                buttonText={MESSAGES.APPLICATION_ADD_BUTTON}
                title={MESSAGES.APPLICATION_EMPTY_SCREEN_TITLE}
                handleOnCreateAction={() => handleMenuClick("Add")}
              />
            </Grid>
          </Grid>
        }
        <Grid container className={(applicationsData && applicationsData.length > 0) || (searchString.length) ? classes.root : classes.hide}>
          <Grid item xs={12} sm={3} className={classes.topLeftHeaderContainer}>
            <SearchBar
              addButtonDialog={handleMenuClick}
              placeHolder={TITLES.APPLICATION_NAME}
              onSearchTextBoxChange={debounceSearch}
              onClearTextInput={handleClearSearchInput}
              showAddButton={createApplicationPermission}
              addButtonMessage={MESSAGES.APPLICATION_ADD_BUTTON}
            />
          </Grid>
          <Grid item xs={12} sm={9} className={classes.detailsBox}>
            {selectedApplication?.id &&
              <>
                <Typography variant="body1" gutterBottom>
                  <Box className={classes.detailsBoxItems}>
                    <Box fontWeight="fontWeightBold">
                      {TITLES.APPLICATION_ID}
                    </Box>
                    <Box fontWeight="fontWeightMedium" className={classes.detailValueText}>
                      {selectedApplication?.id}
                    </Box>
                    <Tooltip title="copy">
                      <Box data-testid="toolTip-sign">
                        <CopyToClipboard text={selectedApplication?.id} onCopy={handleCopy}>
                          <Copy className={`${classes.pointer}`} fontSize="small" />
                        </CopyToClipboard>
                      </Box>
                    </Tooltip>
                  </Box>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <Box className={classes.detailsBoxItems}>
                    <Box fontWeight="fontWeightBold">
                      {TITLES.APPLICATION_ACCOUNT_KEY}
                    </Box>
                    <Box fontWeight="fontWeightMedium" className={classes.detailValueText}>
                      {selectedApplication?.accountKey || "N/A"}
                    </Box>
                    <Tooltip title="copy">
                      <Box data-testid="toolTip-sign">
                        <CopyToClipboard text={selectedApplication?.accountKey} onCopy={handleCopy}>
                          <Copy className={`${classes.pointer}`} fontSize="small" />
                        </CopyToClipboard>
                      </Box>
                    </Tooltip>
                  </Box>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <Box className={classes.detailsBoxItems}>
                    <Box fontWeight="fontWeightBold">
                      {TITLES.REALM_KEY}
                    </Box>
                    <Box fontWeight="fontWeightMedium" className={classes.detailValueText}>
                      {selectedApplication?.realmKey || "N/A"}
                    </Box>
                    <Tooltip title="copy">
                      <Box data-testid="toolTip-sign">
                        <CopyToClipboard text={selectedApplication?.realmId} onCopy={handleCopy}>
                          <Copy className={`${classes.pointer}`} fontSize="small" />
                        </CopyToClipboard>
                      </Box>
                    </Tooltip>
                  </Box>
                </Typography>
              </>
            }
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={3}>
            <Box component="div">
              <Box className={classes.listItemPaper}>
                {((applicationsData && applicationsData.length > 0) || (searchString.length)) ?
                  <SearchList
                    dense={false}
                    minHeight={70}
                    addDivider={true}
                    height={height - 230}
                    disableGutters={false}
                    key={"ApplicationList"}
                    data={applicationsData}
                    onClick={handleListItemClick}
                    selectedItem={selectedApplication}
                    listStyle={classes.searchListContainer}
                    renderItem={(item) => (
                      <SearchListItem
                        key={item?.id}
                        showAvatarContent={true}
                        ListPrimaryContent={
                          <Box>
                            <Typography variant="body1" className={classes.textEllipsisPrimary}>
                              <Box
                                data-testid="name"
                                component="span"
                                fontWeight="fontWeightBold"
                                title={item?.name}
                              >
                                {item?.name}
                              </Box>
                            </Typography>
                          </Box>
                        }
                        ListSecondaryContent={
                          <Box>
                            <Typography variant="body1" color="textSecondary"  className={classes.textEllipsisSecondary}>
                              <Box
                                component="span"
                                fontWeight="fontWeightNormal"
                                data-testid="application-description"
                                title={item?.description}
                              >
                                {item?.description}
                              </Box>
                            </Typography>
                          </Box>
                        }
                        ListSecondaryActionContent={
                          editApplicationPermission || deleteApplicationPermission ?
                            <LuiThreeDotMenu
                              items={_.compact(items)}
                              ariaLabel="Three Dot Menu"
                            /> : null
                        }
                      />
                    )}
                  /> :
                  null
                }
                {searchString.length > 0 && applicationsData.length === 0 && (
                  <EmptyComponent
                    subTitle={''}
                    disableCreateAction={true}
                    title={MESSAGES.NO_RESULTS_FOUND}
                  />
                )}
                {errors && <ErrorMessage {...errors} />}
              </Box>
            </Box>
          </Grid>
          {applicationsData.length ?
            <Grid item xs={12} sm={9}>
              <Box className={classes.tabs}>
                <AppBar
                  elevation={0}
                  position="relative"
                  data-testid="realm-appbar"
                  className={classes.appBar}
                >
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    className={classes.tabs}
                    variant="fullWidth"
                  >
                    {modules.map((module, key) => (
                      <Tab
                        key={key}
                        label={module.name}
                        className={classes.tab}
                      />
                    ))}
                  </Tabs>
                </AppBar>
              </Box>
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Box className={classes.rightContainer}>
                    {modules.map((module, key) => (
                      <TabPanel value={value} index={key} key={key}>
                        {module.component}
                      </TabPanel>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid> : null
          }
          <AddApplicationDialog
            refreshOnSave={() => { }}
            isEdit={action === "Edit"}
            searchString={searchString}
            applicationData={selectedApplication}
            openCloseModal={handleDialogClose}
            openModal={action === "Edit" || action === "Add"}
          />
          {action === "Delete" && (
            <AlertDialog
              showDialogLoader={false}
              handleOnOk={deleteApplication}
              secondButtonText={TITLES.CANCEL}
              primaryButtonText={TITLES.DELETE}
              handleOnCancel={() => setAction("")}
              recordName={selectedApplication.name}
              dialogContentText={TITLES.DELETE_APPLICATION}
              dialogTitle={MESSAGES.DELETE_APPLICATION_TITLE}
            />
          )}
          {snackBarMessage.show && (
            <SnackbarAlert
              show={snackBarMessage.show}
              message={snackBarMessage.message}
              severity={snackBarMessage.severity}
              autoHideDuration={snackBarMessage.autoHideDuration}
            />
          )}
        </Grid>
      </CheckAuthzPermissions>
    </Box>
  );
};

export default Application;
